import axios from 'axios'

type RetryOptions = {
  retries?: number
  baseDelayMs?: number
  maxDelayMs?: number
  jitterRatio?: number
}

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms)
  })

const parseRetryAfterMs = (value: unknown): number | null => {
  if (typeof value !== 'string') return null

  const asInt = Number(value)
  if (!Number.isNaN(asInt)) return Math.max(0, asInt * 1000) // seconds

  const asDate = Date.parse(value)
  if (!Number.isNaN(asDate)) return Math.max(0, asDate - Date.now())

  return null
}

const expBackoffMs = (attempt: number, base: number, max: number) => {
  const raw = base * (2 ** attempt)
  return Math.min(max, raw)
}

const withJitter = (ms: number, jitterRatio: number) => {
  const delta = ms * jitterRatio
  const jitter = (Math.random() * 2 - 1) * delta
  return Math.max(0, Math.round(ms + jitter))
}

export async function withRetry<T>(
  fn: () => Promise<T>,
  shouldRetryFn: (err: unknown) => boolean,
  options: RetryOptions = {},
): Promise<T> {
  const {
    retries = 6,
    baseDelayMs = 300,
    maxDelayMs = 10_000,
    jitterRatio = 0.25,
  } = options

  const attemptOnce = async (attempt: number): Promise<T> => {
    try {
      return await fn()
    } catch (err) {
      const isLastAttempt = attempt >= retries
      if (isLastAttempt || !shouldRetryFn(err)) {
        throw err
      }

      let delay = expBackoffMs(attempt, baseDelayMs, maxDelayMs)

      let status: number | undefined
      if (axios.isAxiosError(err)) {
        status = err.response?.status
        const ra = parseRetryAfterMs(err.response?.headers?.['retry-after'])
        if (ra != null) delay = Math.max(delay, ra)
      }

      if (status === 429) {
        console.info(
          `[withRetry] 429 received (attempt ${attempt + 1}/${retries}), retrying in ${delay}ms`,
        )
      } else if (status) {
        console.debug(
          `[withRetry] HTTP ${status} received (attempt ${attempt + 1}/${retries}), retrying in ${delay}ms`,
        )
      }
      await sleep(withJitter(delay, jitterRatio))
      return attemptOnce(attempt + 1)
    }
  }

  return attemptOnce(0)
}
